import type { CardTheme, ThemeIndex } from './types';

/**
 * The built-in theme. Pure CSS — no image requests, works offline, and acts as
 * the fallback whenever a PNG theme is missing a face.
 */
export const CSS_THEME: CardTheme = {
  id: 'classic',
  name: 'Classic (CSS)',
  kind: 'css',
  credit: 'Built in — no assets required',
  width: 300,
  height: 450,
  radiusPct: 7,
};

/**
 * A worked example of a PNG theme. It ships without artwork, so every face
 * falls back to the CSS renderer until you drop PNGs into
 * `public/themes/example/`. Copy this block into `public/themes/index.json`
 * (or add your own) to register a real one.
 */
export const EXAMPLE_PNG_THEME: CardTheme = {
  id: 'example',
  name: 'Example PNG pack',
  kind: 'png',
  credit: 'Drop 300x450 PNGs into public/themes/example/',
  width: 300,
  height: 450,
  radiusPct: 7,
  basePath: '/themes/example/',
  pattern: '{key}.png',
  back: 'back.png',
};

const STORAGE_KEY = 'uno:theme';

let themes: CardTheme[] = [CSS_THEME, EXAMPLE_PNG_THEME];
const listeners = new Set<() => void>();

export function listThemes(): CardTheme[] {
  return themes;
}

export function getTheme(id: string): CardTheme {
  return themes.find((t) => t.id === id) ?? CSS_THEME;
}

export function savedThemeId(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? CSS_THEME.id;
  } catch {
    return CSS_THEME.id;
  }
}

export function saveThemeId(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode — the choice just won't persist */
  }
}

export function subscribeThemes(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Pulls `public/themes/index.json` at startup and merges it over the built-ins.
 * A missing or malformed file is not an error — you simply get the CSS theme.
 */
export async function loadThemeIndex(): Promise<void> {
  try {
    const res = await fetch('/themes/index.json', { cache: 'no-cache' });
    if (!res.ok) return;
    const data = (await res.json()) as ThemeIndex;
    if (!Array.isArray(data?.themes)) return;

    const merged = new Map<string, CardTheme>(themes.map((t) => [t.id, t]));
    for (const theme of data.themes) {
      if (!theme?.id || (theme.kind !== 'png' && theme.kind !== 'css')) continue;
      merged.set(theme.id, {
        ...theme,
        width: theme.width || 300,
        height: theme.height || 450,
        radiusPct: theme.radiusPct ?? 7,
      });
    }
    themes = [...merged.values()];
    for (const fn of listeners) fn();
  } catch {
    /* offline or no index — built-ins are enough */
  }
}
